/** Mockup 01-B · QR aguardando leitura — ponto pulsando, QR 208px, contexto, links. */
import type { LeadCaptureSession } from '@troia-v3/types';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import { Button, ModalContent, ModalFooter } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import { CaptureStepper, buildCaptureSteps } from './CaptureStepper';

export interface CaptureQrStepProps {
  session: LeadCaptureSession;
  qrDataUrl: string;
  url: string;
  onCancel: () => void;
  onChangeContext: () => void;
  cancelling: boolean;
}

export const CaptureQrStep: React.FC<CaptureQrStepProps> = ({ session, qrDataUrl, url, onCancel, onChangeContext, cancelling }) => {
  const { state } = useAuth();
  const [enlarged, setEnlarged] = useState(false);
  const sellerName = `${state.user?.firstName ?? ''} ${state.user?.lastName ?? ''}`.trim();
  const contextParts = [sellerName, session.unitName ?? session.teamName, session.funnelName].filter((p): p is string => Boolean(p));

  const copyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copiado');
    } catch {
      toast.error('Não foi possível copiar o link');
    }
  };

  return (
    <>
      <ModalContent>
        <CaptureStepper steps={buildCaptureSteps(Boolean(session.capture.channelId), Boolean(session.capture.formId))} current={0} />
        <div className="flex flex-col items-center gap-3">
          <div className="inline-flex items-center gap-2 text-[13px] font-semibold" style={{ color: '#334155' }} data-testid="capture-qr-status">
            <span className="relative inline-flex w-2 h-2">
              <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping motion-reduce:animate-none" style={{ background: '#c4b5fd' }} />
              <span className="relative inline-flex w-2 h-2 rounded-full" style={{ background: '#8b5cf6' }} />
            </span>
            Aguardando leitura…
          </div>
          <div className="rounded-[14px] bg-white" style={{ padding: 14, border: '1px solid #eef1f5' }}>
            {qrDataUrl
              ? <img src={qrDataUrl} alt={`QR Code do atendimento ${session.code}`} width={enlarged ? 320 : 208} height={enlarged ? 320 : 208} className="block" data-testid="capture-qr-image" />
              : <div className="w-[208px] h-[208px] flex items-center justify-center text-xs" style={{ color: '#94a3b8' }}>Gerando QR…</div>}
          </div>
          <div className="flex items-center gap-2 text-xs flex-wrap justify-center" style={{ color: '#64748b' }} data-testid="capture-qr-context">
            {contextParts.map((part, i) => (
              <React.Fragment key={part}>
                {i > 0 && <span style={{ color: '#cbd5e1' }}>·</span>}
                <span>{part}</span>
              </React.Fragment>
            ))}
          </div>
          <div className="text-[13px] text-center max-w-[360px]" style={{ color: '#475569' }}>
            Peça ao cliente para apontar a câmera do celular para o código. Assim que ele enviar os dados, esta tela avança sozinha.
          </div>
          <div className="flex items-center gap-4 text-xs font-semibold" style={{ color: '#7c3aed' }}>
            <button type="button" className="inline-flex items-center gap-1.5 min-h-[44px] sm:min-h-0" onClick={() => { void copyLink(); }} data-testid="capture-qr-copy-link"><Icon name="link" size={14} />Copiar link</button>
            <button type="button" className="inline-flex items-center gap-1.5 min-h-[44px] sm:min-h-0" onClick={() => setEnlarged((v) => !v)} data-testid="capture-qr-enlarge"><Icon name="external-link" size={14} />{enlarged ? 'Reduzir' : 'Ampliar'}</button>
          </div>
          <div className="text-[11px]" style={{ color: '#94a3b8' }}>Válido por 7 dias · uso único</div>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button variant="ghost" icon="x" loading={cancelling} onClick={onCancel} data-testid="capture-qr-cancel">Cancelar atendimento</Button>
        <span className="flex-1" />
        <Button variant="link" onClick={onChangeContext} data-testid="capture-qr-change">Trocar funil ou loja</Button>
      </ModalFooter>
    </>
  );
};
